import { capitalize } from 'lodash';
import {
  LeaseOffer,
  PurchaseOffer,
} from '@upstart/harmony/lib/legos/paymentTypes';
import { FinanceType } from '@upstart/harmony/lib/constants';
import { IncentivesProgramOffer } from '@upstart/harmony/lib/legos/carTypes';
import React, { ReactNode, useMemo, useState } from 'react';
import DataTable from '../../DataTable';
import { filterIncentives, OfferIncentivesPair } from './offer-utils';
import Input from '../Input';

type ApplyIncentivesMultipleTermsProps = {
  selectedIncentives: IncentivesProgramOffer[];
  financeType: FinanceType;
  offers: (PurchaseOffer | LeaseOffer)[];
  onSave(pairs: OfferIncentivesPair[]): void;
};

export default function ApplyIncentivesMultipleTerms({
  selectedIncentives,
  offers,
  financeType,
  onSave,
}: ApplyIncentivesMultipleTermsProps) {
  const [selectedPairs, setSelectedPairs] = useState<OfferIncentivesPair[]>([]);

  const offerPairs: OfferIncentivesPair[] = useMemo(
    () =>
      offers.map(offer => ({
        offer,
        incentives: filterIncentives(selectedIncentives, offer).filter(i =>
          // if incentives are already selected
          offer.programs?.length
            ? // check if the incentive is already part of the program
              offer.programs.find(program => program.id === i.id) ||
              // or check if the incentive is stackable
              !!i.stackablePrograms.find(stackable =>
                offer.programs?.find(program => program.id === stackable),
              )
            : true,
        ),
      })),
    [offers],
  );

  const selectOffer = (params: OfferIncentivesPair) => {
    if (selectedPairs.find(pair => pair.offer === params.offer)) {
      // unselect the offer if it's already selected
      setSelectedPairs(
        selectedPairs.filter(
          selectedOffer => selectedOffer.offer !== params.offer,
        ),
      );
    } else {
      setSelectedPairs([...selectedPairs, params]);
    }
  };

  const renderSelector = (params: OfferIncentivesPair) => {
    const isSelectable = !!params.incentives.length;
    const checked = selectedPairs.find(pair => pair.offer === params.offer);
    if (isSelectable) {
      return (
        <Input
          onChange={() => selectOffer(params)}
          checked={checked}
          type="checkbox"
        />
      );
    }
    return null;
  };

  const renderRow = (offerPair: OfferIncentivesPair) => {
    let note: ReactNode = null;
    if (offerPair.incentives.length === 0) {
      note = 'This offer is not eligible for any of the selected incentives';
    } else if (selectedIncentives.length - offerPair.incentives.length > 0) {
      note = (
        <div className="incentive-editor-warning-message">
          One of your selected incentives cannot be applied to this offer. We
          will still apply all other incentives if you proceed.
        </div>
      );
    }
    const row: ReactNode[] = [
      renderSelector(offerPair),
      capitalize(financeType),
      offerPair.offer.months,
    ];
    if (financeType === FinanceType.LEASE) {
      row.push((offerPair.offer as LeaseOffer).mileage);
    }
    row.push(note);
    return row;
  };

  const rows = offerPairs.map(renderRow);

  const getHeaders = () => {
    const headers = ['', 'Purchase Type', 'Term'];
    if (financeType === FinanceType.LEASE) {
      headers.push('Mileage');
    }
    headers.push('Note');
    return headers;
  };

  const handleClear = () => setSelectedPairs([]);
  const handleSelectAll = () =>
    setSelectedPairs(
      offerPairs.filter(({ incentives }) => !!incentives.length),
    );

  return (
    <>
      <h2>Apply {capitalize(financeType)} Incentives</h2>
      <p>Your Incentives will be Applied To The Following Offers:</p>
      <DataTable
        className="incentives-table"
        emptyMessage="No incentives available"
        rows={rows}
        headers={getHeaders()}
      />
      <div className="modal-footer">
        <button className="modal-button secondary" onClick={handleSelectAll}>
          Select All
        </button>
        <button className="modal-button secondary" onClick={handleClear}>
          Clear Selection
        </button>

        <button
          className="modal-button save"
          onClick={() => onSave(selectedPairs)}
        >
          Apply and Save
        </button>
      </div>
    </>
  );
}
